import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import DateInput from "../../DateInput";
import { isoToEU } from "../../../lib/format";

export default function BlackoutManager({ ranges, onChange }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [err, setErr] = useState("");

  const add = () => {
    setErr("");
    if (!start || !end) { setErr("Both start and end dates are required."); return; }
    if (end < start) { setErr("End date must be on or after start date."); return; }
    const next = [...ranges, { start, end }].sort((a, b) => (a.start || "").localeCompare(b.start || ""));
    onChange(next);
    setStart(""); setEnd("");
  };

  const remove = (idx) => {
    onChange(ranges.filter((_, i) => i !== idx));
  };

  return (
    <div className="mt-2" data-testid="blackout-manager">
      <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 mb-3">Seasonal Blackout Dates</p>

      {ranges.length === 0 && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400 italic mb-3">
          No blackout ranges. The vehicle is available on all dates (subject to bookings).
        </p>
      )}

      {ranges.length > 0 && (
        <ul className="space-y-2 mb-4" data-testid="blackout-list">
          {ranges.map((r, idx) => (
            <li
              key={`${r.start}-${r.end}-${idx}`}
              className="flex items-center justify-between bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 px-3 py-2 rounded"
              data-testid={`blackout-row-${idx}`}
            >
              <span className="text-sm">
                <span className="font-mono">{isoToEU(r.start)}</span>
                <span className="mx-2 text-neutral-400">→</span>
                <span className="font-mono">{isoToEU(r.end)}</span>
              </span>
              <button
                type="button"
                onClick={() => remove(idx)}
                aria-label="Remove blackout range"
                className="p-1 hover:bg-rose-100 dark:hover:bg-rose-900/30 rounded text-rose-600 dark:text-rose-400"
                data-testid={`blackout-remove-${idx}`}
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end p-3 border border-dashed border-black/15 dark:border-white/15 rounded">
        <label className="block">
          <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 block mb-1">Start</span>
          <DateInput value={start} onChange={setStart} inputSize="sm" testId="blackout-start" />
        </label>
        <label className="block">
          <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 block mb-1">End</span>
          <DateInput value={end} onChange={setEnd} inputSize="sm" testId="blackout-end" />
        </label>
        <button
          type="button"
          onClick={add}
          className="bg-black text-white dark:bg-white dark:text-black px-4 py-2 text-sm hover:opacity-90 transition-opacity inline-flex items-center gap-2 rounded h-fit"
          data-testid="blackout-add"
        >
          <Plus size={14} /> Add Range
        </button>
      </div>
      {err && <p className="text-xs text-rose-600 dark:text-rose-400 mt-2" data-testid="blackout-error">{err}</p>}
    </div>
  );
}
